import { Outlet, createRootRouteWithContext, retainSearchParams } from '@tanstack/react-router';
import type { AmberRouterContext } from '@amber-ui/core';
import { VersionCheck } from '@amber-ui/core/src/component/util/VersionCheck';

function RootErrorComponent({ error }: { error: Error; reset: () => void }) {
  return (
    <div style={{ padding: 20, textAlign: 'center' }}>
      <h2>Something went wrong</h2>
      <p>{error.message}</p>
      <a href="/">Go Home</a>
    </div>
  );
}

// Handles truly unmatched root-level URLs (e.g. /xyz) that don't fall under
// any defined route. In practice this is rare — authenticated paths are caught
// by _auth/$.tsx (redirects to /grain_bin/dashboard), and unauthenticated paths
// are explicitly defined (/login, /privacy, etc.). This covers the edge case of
// a manually entered invalid URL before auth completes.
function RootNotFoundComponent() {
  return (
    <div style={{ padding: 20, textAlign: 'center' }}>
      <h2>Page Not Found</h2>
      <p>The page you're looking for doesn't exist.</p>
      <a href="/">Go Home</a>
    </div>
  );
}

export const Route = createRootRouteWithContext<AmberRouterContext>()({
  // Validate all global selection params at the root level.
  // These params are available via useSearch({ strict: false }) from any route.
  // All URL query params arrive as strings from the browser. The `as string`
  // cast is safe here. `|| undefined` strips empty strings so absent params
  // don't linger as '' in the search state.
  validateSearch: (search: Record<string, unknown>) => ({
    bin: (search.bin as string) || undefined,
    barge: (search.barge as string) || undefined,
    pile: (search.pile as string) || undefined,
    acct: (search.acct as string) || undefined,
    pellet: (search.pellet as string) || undefined,
    cable: (search.cable as string) || undefined,
  }),
  search: {
    // Automatically preserve these params across all route transitions.
    // Navigations that don't explicitly set or clear a param will retain it.
    middlewares: [retainSearchParams(['bin', 'barge', 'pile', 'acct', 'pellet', 'cable'])],
  },
  errorComponent: RootErrorComponent,
  notFoundComponent: RootNotFoundComponent,
  component: () => (
    <>
      <VersionCheck />
      <Outlet />
    </>
  ),
});
